import { Autocomplete } from "$components/autocomplete/autocomplete";
import {
  IconButton,
  type IconButtonProps,
} from "$components/button/icon-button";
import { BinIcon, ConfirmationIcon, ErrorIcon } from "$components/icons";
import { SelectField as RHFSelectField } from "$components/rhf-fields";
import TextField from "$components/text-field";
import { useUser } from "$global-providers/user-provider";
import { useClinicalDecisionSupportService } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/provider";
import {
  Box,
  CircularProgress,
  InputAdornment,
  makeStyles,
  Typography,
} from "@material-ui/core";
import type { Disease } from "elephant-web/gql";
import type Fuse from "fuse.js";
import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { shouldAddDiseaseToCDS } from "../../_lib/clinical-decision-support-service/mappers";
import { getDiseaseCode } from "../../vaccinations/_lib/get-disease-code";
import DiagnosisListOption, {
  type ActiveDiseaseOption,
} from "./diagnosis-list-option";
import {
  mapDiseaseCodeToUri,
  mapDiseaseToPatientClinicalResource,
} from "./fhir-mapper";

const BinButton = (props: IconButtonProps) => (
  <IconButton color="error" aria-label="delete" title="delete" {...props}>
    <BinIcon fill="currentColor" />
  </IconButton>
);

const DIAGNOSES_MAX_RESULTS = 100;

const useShouldShowCategory = (index: number) => {
  const { control } = useFormContext();
  const freeText = useWatch({
    control,
    name: `diagnosis.${index}.freeText`,
  });

  return freeText;
};

const useIconStyles = makeStyles((theme) => ({
  icon: {
    marginRight: ({ index }: { index: number }) =>
      index > 0 ? theme.spacing(4) : 0,
  },
  confirmIcon: {
    color: theme.palette.success.main,
  },
}));

interface CategoryProps {
  categoryList: CategoryItem[];
  index: number;
}

export const FieldAdornment: React.FC<{
  index: number;
  isLoading: boolean;
}> = ({ index, isLoading }) => {
  const showCategoryField = useShouldShowCategory(index);
  const {
    formState: { touchedFields },
    control,
  } = useFormContext();
  const touched = !!touchedFields.diagnosis?.[index]?.codification?.name;
  const [codificationName, disease] = useWatch({
    control,
    name: [
      `diagnosis.${index}.codification.name`,
      `diagnosis.${index}.disease`,
    ],
  });

  const classes = useIconStyles({ index });

  let fieldAdornment = null;
  if (isLoading) {
    fieldAdornment = (
      <CircularProgress
        color="inherit"
        size={20}
        data-testid="diagnosis-list-item-loading"
      />
    );
  } else if (touched && (disease || codificationName)) {
    fieldAdornment = showCategoryField ? (
      <ErrorIcon
        className={classes.icon}
        color="error"
        titleAccess="unclassified diagnosis"
      />
    ) : (
      <ConfirmationIcon
        className={`${classes.icon} ${classes.confirmIcon}`}
        titleAccess="existing diagnosis"
      />
    );
  }

  return fieldAdornment;
};

const Category = ({ categoryList, index }: CategoryProps) => {
  const { formatMessage } = useIntl();

  const mappedCategories = React.useMemo(() => {
    return (
      categoryList?.map(({ displayName, value }) => ({
        label: displayName,
        value,
      })) ?? []
    ).concat({
      label: "Unclassified",
      value: "unclassified",
    });
  }, [categoryList]);

  const { register, unregister } = useFormContext();
  const showCategoryField = useShouldShowCategory(index);

  // The category field is conditionally shown for free-text diagnoses
  // If it is not an input on the page, this is a virtual input we need to register
  const categoryName = `diagnosis.${index}.codification.category`;
  React.useEffect(() => {
    if (showCategoryField) {
      unregister(categoryName, { keepValue: true });
    } else {
      register(categoryName, { shouldUnregister: false });
    }
  }, [register, unregister, categoryName, showCategoryField]);

  if (!showCategoryField) {
    return null;
  }

  return (
    <Box>
      <Box my={1}>
        <RHFSelectField
          name={`diagnosis.${index}.codification.category`}
          selectProps={{ defaultValue: "unclassified" }}
          label={formatMessage({
            id: "diagnosis.list.categories.label",
            defaultMessage: "Category",
          })}
          options={mappedCategories}
          fullWidth
        />
      </Box>
      <Box display="flex" justifyContent="flex-start" alignItems="center">
        <ErrorIcon color="error" />
        <Box ml={2}>
          <Typography variant="body2">
            <FormattedMessage
              id="patient.new.consultation.diagnosis.unclassified.warning"
              defaultMessage="You are creating a diagnosis that is unclassified. {br} Please ensure it doesn't already exist before proceeding."
              values={{ br: <br /> }}
            />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export type CategoryItem = {
  displayName: string;
  value: string;
};

const RenderDiagnosesListItem: React.FC<{
  categoryList: CategoryItem[];
  index: number;
  searchEngine: Fuse<ActiveDiseaseOption>;
  remove: (i: number) => void;
  isLoading: boolean;
  shouldFocus?: boolean;
}> = ({
  categoryList,
  index,
  searchEngine,
  remove,
  isLoading,
  shouldFocus = false,
}) => {
  const { getValues, setValue, control, register } = useFormContext();
  const { formatMessage } = useIntl();
  const user = useUser();
  const { patientId } = useParams();

  const {
    patientClinicalResources: { add: addResource, remove: removeResource },
  } = useClinicalDecisionSupportService();

  const codificationName = `diagnosis.${index}.codification.name`;
  const codificationCode = `diagnosis.${index}.codification.code`;
  const diseaseName = `diagnosis.${index}.disease`;
  const freeTextName = `diagnosis.${index}.freeText`;
  const categoryName = `diagnosis.${index}.codification.category`;

  // Register the virtual fields
  React.useEffect(() => {
    register(diseaseName);
    register(freeTextName);
  }, [register, diseaseName, freeTextName]);

  const setDisease = (selectedDisease: Disease) => {
    if (selectedDisease) {
      setValue(freeTextName, false);
      if (shouldAddDiseaseToCDS(getDiseaseCode(selectedDisease))) {
        addResource(
          "icd11",
          mapDiseaseToPatientClinicalResource(
            getDiseaseCode(selectedDisease),
            user.id,
          ),
        );
      }
    }

    setValue(codificationName, selectedDisease?.displayName);
    setValue(codificationCode, getDiseaseCode(selectedDisease));
    setValue(diseaseName, selectedDisease?.uuid ?? "");
    // `shouldValidate` only required for last change, as we are changing 3 values at once
    // but only need to validate once all values are set.
    setValue(categoryName, selectedDisease?.category ?? "unclassified", {
      shouldValidate: true,
    });
  };

  const handleInputChange = async () => {
    setValue(categoryName, "unclassified");
    setValue(diseaseName, undefined);

    const code = getValues(codificationCode);
    if (shouldAddDiseaseToCDS(code)) {
      removeResource(mapDiseaseCodeToUri(code));
    }
  };

  const handleFieldBlur = () => {
    const diagnosis = getValues(`diagnosis.${index}`);
    if (!diagnosis) return;
    const { disease, codification } = diagnosis;
    const freeText = !!(codification?.name && !disease);
    setValue(freeTextName, freeText);
  };

  const labelMsg = formatMessage({
    id: "diagnosis.list.name.label",
    defaultMessage: "What's the diagnosis?",
  });

  const placeholderMsg = formatMessage({
    id: "diagnosis.list.name.placeholder",
    defaultMessage: "Add diagnosis here",
  });

  return (
    <li data-testid={`DiagnosisListItem-${index}`}>
      <Box position="relative">
        <Controller
          name={codificationName}
          control={control}
          defaultValue={getValues(codificationName)}
          render={({ field, fieldState: { error, isTouched } }) => {
            const err = isTouched ? error?.message : undefined;
            const handleChange = (
              e: React.ChangeEvent<HTMLInputElement>,
              _: string,
              reason: string,
            ) => {
              if (reason === "reset") return;
              field.onChange(e);
              handleInputChange();
            };
            const handleBlur = (_: React.FocusEvent<HTMLInputElement>) => {
              field.onBlur();
              handleFieldBlur();
            };
            return (
              <Autocomplete
                disableClearable
                freeSolo
                searchEngine={searchEngine}
                getOptionLabel={(option) => option?.item?.displayName}
                onChange={setDisease}
                onBlur={handleBlur}
                onInputChange={handleChange}
                inputValue={field.value ?? ""}
                maxResults={DIAGNOSES_MAX_RESULTS}
                renderOption={(option) => (
                  <DiagnosisListOption option={option} />
                )}
                loading={isLoading}
                renderInput={(params) => (
                  <TextField
                    name={codificationName}
                    data-testid={`new.consultation.diagnosis.input-${index}`}
                    autoFocus={shouldFocus}
                    required
                    label={labelMsg}
                    placeholder={placeholderMsg}
                    autoComplete="off"
                    error={err}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <FieldAdornment index={index} isLoading={isLoading} />
                          {index > 0 && (
                            <Box
                              position="absolute"
                              top={0}
                              right={0}
                              height="100%"
                              display="flex"
                              alignSelf="center"
                            >
                              <BinButton
                                data-testid={`patient-diagnosis-${index}-delete`}
                                onClick={() => {
                                  const code = getValues(codificationCode);
                                  if (shouldAddDiseaseToCDS(code)) {
                                    removeResource(mapDiseaseCodeToUri(code));
                                  }

                                  remove(index);
                                }}
                              />
                            </Box>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            );
          }}
        />
      </Box>
      <Category categoryList={categoryList} index={index} />
    </li>
  );
};

export default React.memo(RenderDiagnosesListItem);
